"use client"

import { useMemo, useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useOrganization } from "@/features/settings/hooks/use-organization"
import { authStore } from "@/store/auth"
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckout,
} from "@stripe/react-stripe-js/checkout"
import { loadStripe } from "@stripe/stripe-js"
import { LoaderCircle } from "lucide-react"

import { useBillingStore } from "../../store/billing-store"
import { PlanSummary } from "./plan-summary"

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)

interface CheckoutPaymentFormProps {
  onPaymentSuccess: () => void
  onPaymentError: (error: string) => void
}

export function CheckoutPaymentForm({
  onPaymentSuccess,
  onPaymentError,
}: CheckoutPaymentFormProps) {
  const [error, setError] = useState<string | null>(null)
  const { user } = authStore.getState()
  const { data: organization, isLoading: isOrganizationLoading } = useOrganization(
    user?.organization_id
  )

  const { selectedPlan, createTransparentCheckout } = useBillingStore()

  // Stripe react-stripe-js v6: CheckoutElementsProvider takes a clientSecret
  // Promise instead of a fetchClientSecret callback. Memoize on the plan/user
  // identity so the checkout session is created once, not on every render.
  const clientSecret = useMemo(() => {
    if (!selectedPlan || !user || !organization) return null

    return createTransparentCheckout({
      plan_id: selectedPlan.id,
      customer_info: {
        email: user.email,
        name: user.name,
        phone: organization.whatsapp,
      },
      interval: selectedPlan.interval,
      trial_days: 7,
    })
      .then(result => {
        if (!result?.client_secret) {
          throw new Error("Falha ao criar sessão de pagamento")
        }
        return result.client_secret
      })
      .catch(err => {
        const message = err instanceof Error ? err.message : "Falha ao criar sessão de pagamento"
        setError(message)
        onPaymentError(message)
        throw err
      })
  }, [selectedPlan?.id, selectedPlan?.interval, user?.email, user?.name, organization?.whatsapp])

  if (isOrganizationLoading) {
    return <div>Carregando...</div>
  }

  if (error || !clientSecret) {
    return (
      <div className="mx-auto my-8 max-w-md">
        <Alert variant="destructive">
          <AlertDescription>
            {error ||
              "Não foi possível inicializar o formulário de pagamento. Por favor, tente novamente."}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const options = {
    clientSecret,
    elementsOptions: {
      appearance: {
        theme: "stripe" as const,
      },
    },
  }

  return (
    <div className="mx-auto max-w-5xl overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
      <div className="grid md:grid-cols-2">
        {/* Left column - Plan details */}
        <PlanSummary selectedPlan={selectedPlan} />

        {/* Right column - Checkout button */}
        <div className="bg-white p-8 dark:bg-gray-900">
          <div className="mx-auto max-w-md">
            <CheckoutElementsProvider stripe={stripePromise} options={options}>
              <PaymentForm onPaymentSuccess={onPaymentSuccess} onPaymentError={onPaymentError} />
            </CheckoutElementsProvider>
            <div className="mt-4 flex items-center justify-center space-x-2">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                ></path>
              </svg>
              <span className="text-xs text-gray-500">Conexão segura e criptografada</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentForm({
  onPaymentSuccess,
  onPaymentError,
}: {
  onPaymentSuccess: () => void
  onPaymentError: (error: string) => void
}) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const checkoutState = useCheckout()
  const { submitCheckoutSuccess } = useBillingStore()

  if (checkoutState.type === "loading") {
    return (
      <div className="flex items-center justify-center py-10">
        <LoaderCircle className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (checkoutState.type === "error") {
    return (
      <Alert variant="destructive">
        <AlertDescription>{checkoutState.error.message}</AlertDescription>
      </Alert>
    )
  }

  const checkout = checkoutState.checkout

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsProcessing(true)
    setError(null)

    try {
      const result = await checkout.confirm({
        returnUrl: window.location.origin + "/billing/success",
        redirect: "if_required",
        savePaymentMethod: true,
      })
      if (result.type === "error") {
        setError(result.error.message)
      } else {
        await submitCheckoutSuccess({
          session_id: result.session.id,
        })
        onPaymentSuccess()
      }
    } catch (err) {
      onPaymentError(err instanceof Error ? err.message : "Falha no processamento do cartão")
    } finally {
      setIsProcessing(false)
    }
  }
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement
        options={{
          layout: {
            type: "tabs",
            defaultCollapsed: false,
          },
          paymentMethodOrder: ["card", "boleto"],
          fields: {
            billingDetails: {
              address: {
                country: "auto",
              },
              name: "auto",
              email: "auto",
              phone: "auto",
            },
          },
          terms: {
            card: "never",
            bancontact: "never",
          },
        }}
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="h-12 w-full rounded-md bg-indigo-600 text-white transition-colors hover:bg-indigo-700"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : (
          `Iniciar Teste Grátis`
        )}
      </Button>
    </form>
  )
}
